import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Tabs, Tab } from 'components/tabs'
import LinearProgress from '@material-ui/core/LinearProgress'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import { useAuth } from 'lib/auth'
import RouterLinkButton from 'components/router-link-button'
import ExternalLinkButton from 'components/external-link-button'
import { PageHeader } from 'components/page-header'
import SchedulePage from './schedule'
import OfferingsPage, {
  OFFERINGS_TAB,
  getOfferingsPath,
  parseOfferingsTab,
} from './offerings'
import OfferingsHeaderActions from './offerings/offerings-header-actions'
import useGroupEventsPanel from './events/use-group-events-panel'
import { gtmEvent } from 'lib/gtm'
import GET_COACH_ACCOUNT from './query'
import SettingsOutlinedIcon from 'components/material-symbol/icons/Settings'
import { ReactComponent as CalendarSyncIcon } from 'images/icons/calendar-sync.svg'
import IconButton from '@material-ui/core/IconButton'
import SubscribeDialog from './schedule/subscribe-dialog'

const useStyles = makeStyles(theme => ({
  button: {
    display: 'inherit',
    minWidth: 'max-content',
  },
  disabledButton: {
    background: '#0000001F',
    color: '#00000042',
    '&:hover': {
      background: '#0000001F',
    },
  },
  buttonsWrapper: {
    display: 'flex',
    marginRight: 0,
    width: '100%',
    paddingLeft: '0.5rem',
    justifyContent: 'space-around',
    gap: '10px',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'end',
    },
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 0,
      justifyContent: 'center',
    },
  },
}))

const calendarSyncIconProps = {
  style: { display: 'block', width: 20, height: 20 },
}

const BookingsPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { user, setUserProperties } = useAuth()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('xs'))
  let pageName = window.location.pathname.split('/').pop()
  const { data, loading } = useQuery(GET_COACH_ACCOUNT)
  const activeGroupEvents = data?.currentUser?.groupEvents.filter(
    e => !e.isCanceled,
  )
  pageName = pageName === 'offerings' ? 'My Offerings' : pageName
  const isSchedulePage = pageName === 'schedule'
  const isOfferingsPage = pageName === 'My Offerings' || pageName === 'bookings'
  if (isOfferingsPage && pageName === 'bookings') {
    pageName = 'My Offerings'
  }
  const [selectedTab, setSelectedTab] = useState(0)
  const [offeringsTab, setOfferingsTab] = useState(OFFERINGS_TAB.BOOKING_TYPE)
  const [
    shouldShowBookingTypeDisclaimer,
    setShouldShowBookingTypeDisclaimer,
  ] = useState(false)
  const groupEventsPanel = useGroupEventsPanel({ skip: !isOfferingsPage })

  const onboardingSchedule = {
    isFacilitiesCreated:
      (data?.currentUser?.attributes?.facilities?.length ?? 0) > 0,
    isGroupEventCreated: activeGroupEvents?.length > 0,
    isAvailable:
      data?.coachAvailabilities?.some(a => a.timeSlots.length > 0) ?? false,
    isBookingTypeCreated: (data?.lessonTypes?.length ?? 0) > 0,
    hasCoachProfile: Boolean(data?.currentUser?.attributes?.coachProfile),
  }

  const isOnboardingComplete =
    onboardingSchedule.isGroupEventCreated ||
    onboardingSchedule.isBookingTypeCreated

  const handleBookStudentClick = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
  }

  const handleCreateBookingTypeClick = () => {
    if (!canCreateBookingType) return
    setUserProperties({ onboarding: false })
    history.push('/pga-coach/bookings/booking-types/new')
  }

  const createBookingTypeDisabledReason = loading
    ? ''
    : (() => {
      if (!onboardingSchedule.hasCoachProfile) {
        return 'Create your coach profile before adding a booking type.'
      }
      if (!onboardingSchedule.isAvailable) {
        return 'Set your availability before creating a booking type.'
      }
      if (shouldShowBookingTypeDisclaimer) {
        return 'To add another offering, you need to upgrade your PGA Coach account.'
      }
      return ''
    })()

  const canCreateBookingType = !loading && !createBookingTypeDisabledReason

  const actionButton = isSchedulePage ? (
    <>
      {!isMobile ? (
        <>
          <SubscribeDialog
            trigger={
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                startIcon={<CalendarSyncIcon {...calendarSyncIconProps} />}
                className={classes.button}
              >
                Subscribe
              </Button>
            }
          />
          <RouterLinkButton
            variant="outlined"
            color="primary"
            size="medium"
            to="/pga-coach/settings/availability"
            startIcon={<SettingsOutlinedIcon fontSize="small" />}
            className={classes.button}
          >
            Availability Settings
          </RouterLinkButton>
        </>
      ) : null}
      {!isMobile && isOnboardingComplete ? (
        <>
          <ExternalLinkButton
            variant="contained"
            color="primary"
            size="medium"
            className={classes.button}
            href={user.coach.bookingUrl}
            target="_self"
          >
            Book a Student
          </ExternalLinkButton>
        </>
      ) : null}
      {isMobile && isOnboardingComplete ? (
        <>
          <ExternalLinkButton
            variant="contained"
            color="primary"
            href={user.coach.bookingUrl}
            onClick={handleBookStudentClick}
            target="_self"
            data-testid="book-student"
          >
            Book a Student
          </ExternalLinkButton>
        </>
      ) : null}
    </>
  ) : isOfferingsPage ? (
    <OfferingsHeaderActions
      user={user}
      isOnboardingComplete={isOnboardingComplete}
      isBookingTypeTab={offeringsTab === OFFERINGS_TAB.BOOKING_TYPE}
      canCreateBookingType={canCreateBookingType}
      createBookingTypeDisabledReason={createBookingTypeDisabledReason}
      onCreateBookingType={handleCreateBookingTypeClick}
      onCreateEvent={groupEventsPanel.handleCreateEventClick}
      onBookStudentClick={handleBookStudentClick}
    />
  ) : null

  const actions = actionButton ? (
    <Box className={classes.buttonsWrapper}>{actionButton}</Box>
  ) : null
  const isMobileActionEmpty = (() => {
    if (!actionButton) return true
    if (actionButton.type === OfferingsHeaderActions) return false
    const { children } = actionButton.props
    if (!children) return true
    return React.Children.toArray(children).every(child => child === null)
  })()

  useEffect(() => {
    if (!isOfferingsPage) return

    const tabFromUrl = parseOfferingsTab(location.search)
    setOfferingsTab(tabFromUrl ?? OFFERINGS_TAB.BOOKING_TYPE)
  }, [isOfferingsPage, location.search])

  if (loading) return <LinearProgress color="primary" />

  return (
    <>
      <PageHeader
        title={pageName}
        tabs={
          isSchedulePage ? (
            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value)}
            >
              <Tab label="Upcoming" />
              <Tab label="Past" />
            </Tabs>
          ) : isOfferingsPage ? (
            <Tabs
              value={offeringsTab}
              onChange={(_, value) => {
                setOfferingsTab(value)
                history.replace(getOfferingsPath(value))
              }}
            >
              <Tab label="Booking Type" />
              <Tab label="Group Events" />
            </Tabs>
          ) : null
        }
        actions={actions}
        fixedMobileActions={isMobile && !isMobileActionEmpty}
        mobileCornerAction={
          <>
            <SubscribeDialog
              trigger={
                <Box mr={2}>
                  <IconButton style={{ padding: 0 }} color="primary">
                    <CalendarSyncIcon {...calendarSyncIconProps} />
                  </IconButton>
                </Box>
              }
            />
            <IconButton
              style={{ padding: 0 }}
              color="primary"
              href="/pga-coach/settings/availability"
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
      {isSchedulePage ? (
        <SchedulePage
          selectedTab={selectedTab}
          onboardingSchedule={onboardingSchedule}
        />
      ) : isOfferingsPage ? (
        <OfferingsPage
          selectedTab={offeringsTab}
          hasCoachProfile={onboardingSchedule.hasCoachProfile}
          setShouldShowBookingTypeDisclaimer={
            setShouldShowBookingTypeDisclaimer
          }
          groupEventsPanel={groupEventsPanel}
        />
      ) : null}
    </>
  )
}

export default BookingsPage
